import {
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Info,
  RotateCcw,
  Search,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/shadcn/ui/button';
import { Checkbox } from '@/components/shadcn/ui/checkbox';
import { Input } from '@/components/shadcn/ui/input';
import { ScrollArea } from '@/components/shadcn/ui/scroll-area';
import {
  getStructuredFilterLeaves,
  getStructuredFilterVisibilityState,
  STRUCTURED_FILTER_MENU_SECTIONS,
  toggleStructuredFilterVisibility,
  useStructuredFilterVisibility,
} from '@/entities/(filter)/common';
import useStructuredFilterMap from '@/features/StructuredFilter/configs/useStructuredFilterMap';
import StructuredFilter from '@/features/StructuredFilter/StructuredFilter';
import { cn } from '@/lib/cn';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { toast } from '@/stores/pure/ToastStore';

const FILTER_VISIBILITY_DESCRIPTION =
  'Control which filters appear in your personal filter menu.';

const getSectionFields = (fieldOptions: StructuredFieldOptionMap) => {
  const sectionKeys = STRUCTURED_FILTER_MENU_SECTIONS;
  return [
    sectionKeys.firstSection,
    sectionKeys.engagementSection,
    sectionKeys.managerSection,
  ].map((section) =>
    section
      .map((key) => fieldOptions[key])
      .filter((field): field is StructuredField => Boolean(field)),
  );
};

const filterFieldBySearch = (
  field: StructuredField,
  search: string,
): StructuredField | null => {
  if (!search) return field;

  const fieldMatches = field.columnName.toLocaleLowerCase().includes(search);
  const children = Object.entries(field.childField ?? {})
    .map(([key, child]) => [key, filterFieldBySearch(child, search)] as const)
    .filter((entry): entry is readonly [string, StructuredField] =>
      Boolean(entry[1]),
    );

  if (fieldMatches) return field;
  if (children.length === 0) return null;

  return {
    ...field,
    childField: children.reduce<Record<string, StructuredField>>(
      (acc, [key, child]) => {
        acc[key] = child;
        return acc;
      },
      {},
    ),
  };
};

type FilterTreeRowProps = {
  field: StructuredField;
  depth?: number;
  hiddenLeafIds: ReadonlySet<string>;
  visibleLeafCount: number;
  expandedGroups: ReadonlySet<string>;
  onToggleExpanded: (columnKey: string) => void;
  onToggleVisibility: (field: StructuredField, isVisible: boolean) => void;
};

const FilterTreeRow = ({
  field,
  depth = 0,
  hiddenLeafIds,
  visibleLeafCount,
  expandedGroups,
  onToggleExpanded,
  onToggleVisibility,
}: FilterTreeRowProps) => {
  const children = Object.values(field.childField ?? {});
  const isGroup = children.length > 0;
  const visibility = getStructuredFilterVisibilityState(field, hiddenLeafIds);
  const checked = visibility.isIndeterminate
    ? 'indeterminate'
    : visibility.isChecked;
  const isFinalVisibleField =
    visibility.isChecked && visibleLeafCount - visibility.visibleCount < 1;
  const isExpanded = expandedGroups.has(field.columnKey);

  return (
    <div>
      <div
        className={cn(
          'group flex min-h-10 items-center gap-2 border-b border-border/70 px-3 text-sm transition-colors hover:bg-muted/60',
          isGroup && 'font-medium',
        )}
        style={{ paddingLeft: `${12 + depth * 24}px` }}
      >
        {isGroup ? (
          <button
            type="button"
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${field.columnName}`}
            className="flex size-5 shrink-0 items-center justify-center text-muted-foreground"
            onClick={() => onToggleExpanded(field.columnKey)}
          >
            {isExpanded ? (
              <ChevronDown className="size-4" />
            ) : (
              <ChevronRight className="size-4" />
            )}
          </button>
        ) : (
          <span className="size-5 shrink-0" aria-hidden="true" />
        )}
        <span className="flex size-5 shrink-0 items-center justify-center text-muted-foreground">
          {field.icon}
        </span>
        <span className="min-w-0 flex-1 truncate">{field.columnName}</span>
        {isGroup && (
          <span className="mr-1 text-xs font-normal text-muted-foreground">
            {visibility.visibleCount}/{visibility.leafCount}
          </span>
        )}
        <Checkbox
          aria-label={`${visibility.isChecked ? 'Hide' : 'Show'} ${field.columnName}`}
          checked={checked}
          disabled={isFinalVisibleField}
          onCheckedChange={() =>
            onToggleVisibility(field, !visibility.isChecked)
          }
        />
      </div>
      {isGroup && isExpanded && (
        <div>
          {children.map((child) => (
            <FilterTreeRow
              key={child.columnKey}
              field={child}
              depth={depth + 1}
              hiddenLeafIds={hiddenLeafIds}
              visibleLeafCount={visibleLeafCount}
              expandedGroups={expandedGroups}
              onToggleExpanded={onToggleExpanded}
              onToggleVisibility={onToggleVisibility}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FilterMenuPreview = () => {
  const [filterValues, setFilterValues] = useState<
    StructuredFilterQueryFormatT[]
  >([]);

  return (
    <div className="flex min-h-0 flex-1 flex-col rounded-xl border border-border bg-background p-5">
      <div>
        <h2 className="text-base font-semibold text-foreground">
          Live preview
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Your current filter menu.
        </p>
      </div>
      <div className="mt-5 min-h-0 flex-1 overflow-auto">
        <StructuredFilter
          values={filterValues}
          onChange={setFilterValues}
          clearFilter={() => setFilterValues([])}
        />
      </div>
    </div>
  );
};

export function FilterVisibilityPage() {
  const fieldOptions = useStructuredFilterMap();
  const {
    hiddenLeafIds,
    hiddenLeafIdSet,
    setHiddenLeafIds,
    clearHiddenLeafIds,
  } = useStructuredFilterVisibility();
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(),
  );
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const sections = useMemo(
    () => getSectionFields(fieldOptions),
    [fieldOptions],
  );
  const filteredSections = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase();
    return sections
      .map((section) =>
        section
          .map((field) => filterFieldBySearch(field, normalizedSearch))
          .filter((field): field is StructuredField => Boolean(field)),
      )
      .filter((section) => section.length > 0);
  }, [search, sections]);
  const knownLeafIds = useMemo(
    () =>
      new Set(
        sections.flatMap((section) =>
          section.flatMap((field) =>
            getStructuredFilterLeaves(field).map((leaf) => leaf.columnKey),
          ),
        ),
      ),
    [sections],
  );
  const hiddenCount = hiddenLeafIds.filter((id) => knownLeafIds.has(id)).length;
  const visibleLeafCount = knownLeafIds.size - hiddenCount;
  const allLeafIds = [...knownLeafIds];

  const toggleExpanded = (columnKey: string) => {
    setExpandedGroups((current) => {
      const next = new Set(current);
      if (next.has(columnKey)) next.delete(columnKey);
      else next.add(columnKey);
      return next;
    });
  };

  const toggleVisibility = (field: StructuredField, isVisible: boolean) => {
    setHiddenLeafIds(
      toggleStructuredFilterVisibility(
        field,
        hiddenLeafIds,
        isVisible,
        allLeafIds,
      ),
    );
    toast('Filter visibility saved.', 'success');
  };

  const handleResetConfirm = () => {
    clearHiddenLeafIds();
    setIsResetDialogOpen(false);
    toast('Filters reset to default.', 'success');
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-background text-foreground">
      <header className="flex shrink-0 items-center justify-between border-b border-border px-8 py-2.5">
        <h1 className="mt-1 text-sm font-semibold tracking-tight">
          Filter visibility
        </h1>
        <Button type="button" onClick={() => setIsResetDialogOpen(true)}>
          <RotateCcw className="size-4" />
          Reset to default
        </Button>
      </header>
      <div className="min-h-0 flex-1 overflow-hidden p-6">
        <div className="grid h-full min-h-0 gap-4 xl:grid-cols-[minmax(0,1.08fr)_minmax(360px,0.92fr)]">
          <section className="flex min-h-0 flex-col rounded-xl border border-border bg-background">
            <div className="shrink-0 border-b border-border px-5 py-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-base font-semibold">Filter visibility</h2>
                  <p className="mt-1 max-w-xl text-sm leading-5 text-muted-foreground">
                    {FILTER_VISIBILITY_DESCRIPTION}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2 text-sm text-primary">
                  {hiddenCount > 0 ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                  <span>{hiddenCount} hidden</span>
                </div>
              </div>
              <div className="relative mt-4">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search filters"
                  className="h-10 pl-9"
                />
              </div>
            </div>
            <ScrollArea className="min-h-0 flex-1">
              <div className="p-2">
                {filteredSections.length === 0 ? (
                  <div className="flex items-center gap-2 px-3 py-8 text-sm text-muted-foreground">
                    <Info className="size-4" />
                    No filters found.
                  </div>
                ) : (
                  filteredSections.map((section, sectionIndex) => (
                    <div key={`filter-section-${sectionIndex}`}>
                      {sectionIndex > 0 && (
                        <div className="my-2 h-px bg-border" />
                      )}
                      {section.map((field) => (
                        <FilterTreeRow
                          key={field.columnKey}
                          field={field}
                          hiddenLeafIds={hiddenLeafIdSet}
                          visibleLeafCount={visibleLeafCount}
                          expandedGroups={expandedGroups}
                          onToggleExpanded={toggleExpanded}
                          onToggleVisibility={toggleVisibility}
                        />
                      ))}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </section>
          <FilterMenuPreview />
        </div>
      </div>
      <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <DialogContent size="s">
          <DialogHeader>
            <DialogTitle>Reset filters to default?</DialogTitle>
            <DialogDescription>
              This will make all filters visible in your personal filter menu.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <div />
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsResetDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="button" onClick={handleResetConfirm}>
                Reset to default
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
